/**
 * 프론트 팝업 매니저(§7·§9·§11·§12·§14·§15).
 *
 * 서버가 넣은 설정(JSON) + <template> 을 읽어, 클라이언트에서 디바이스/방문자/숨김을 판정하고,
 * 트리거 시점에 필요한 팝업만 <template> 을 clone 해 표시한다(이미지도 이때 다운로드 — §4).
 * 표시 정책(individual/priority/slider), 위치/크기 적용, 애니메이션, 닫기/오늘하루/모달 focus-trap,
 * 통계 비콘을 담당한다. 전체가 try/catch 로 격리되어 오류가 사이트에 영향을 주지 않는다(§19).
 *
 * 참고: 사용자 드래그 이동(§6)·통계 대시보드(§16)는 Phase 2.
 */
import { PopupStorage } from "./PopupStorage";
import { PopupAnalytics } from "./PopupAnalytics";

type Device = "desktop" | "tablet" | "mobile";

interface PlacementCfg {
    preset: string; anchor: string; x_offset: number; y_offset: number;
    z_index: number; edge_margin: number; fixed: boolean; safe_area: boolean;
}
interface MediaCfg {
    url: string; width: string; max_width: string; height: string; max_height: string;
    aspect_ratio: string; object_fit: string; radius: string; padding: string; mobile_full_width: boolean;
}
interface ClientCfg {
    animation: { type: string; duration: number; delay: number };
    movable: string; close_button: boolean; backdrop_close: boolean; esc_close: boolean;
    hide_policy: string; hide_days: number; content_version: number;
    device_targets: string[]; visitor_target: string; trigger: { type: string; value: string };
}
interface PopupCfg {
    id: number; template: string; group: string; displayMode: string; priority: number; sort: number;
    locale: string; placements: Record<string, PlacementCfg>; media: Record<string, MediaCfg>; client: ClientCfg;
}

type Unit = { kind: "single"; cfg: PopupCfg } | { kind: "group"; cfgs: PopupCfg[] };

function currentDevice(): Device {
    const w = window.innerWidth || document.documentElement.clientWidth || 1280;
    if (w <= 767) return "mobile";
    if (w <= 1023) return "tablet";
    return "desktop";
}

function isTouch(): boolean {
    return "ontouchstart" in window || (navigator.maxTouchPoints || 0) > 0;
}

function prefersReducedMotion(): boolean {
    return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function presetParts(preset: string): [string, string] {
    if (preset === "center") return ["middle", "center"];
    const [v, h] = preset.split("-");
    return [v || "middle", h || "center"];
}

const FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select,textarea,[tabindex]:not([tabindex="-1"])';

export class PopupManager {
    private root!: HTMLElement;
    private layer!: HTMLElement;
    private device: Device = "desktop";
    private analytics!: PopupAnalytics;
    private openModals = 0;

    static init(): void {
        try {
            const mgr = new PopupManager();
            mgr.run();
        } catch {
            /* 팝업 오류는 사이트에 영향 없음(§19) */
        }
    }

    private run(): void {
        const root = document.querySelector<HTMLElement>("[data-popups-root]");
        if (!root) return;
        this.root = root;
        const cfgEl = root.querySelector<HTMLScriptElement>("[data-popups-config]");
        if (!cfgEl) return;

        let configs: PopupCfg[] = [];
        try {
            configs = JSON.parse(cfgEl.textContent || "[]");
        } catch {
            return;
        }
        if (!configs.length) return;

        this.device = currentDevice();
        const endpoint = root.getAttribute("data-popups-endpoint") || "/popups/event";
        const locale = root.getAttribute("data-popups-locale") || document.documentElement.lang || "ko";
        this.analytics = new PopupAnalytics(endpoint, locale, this.device);

        const returning = PopupStorage.isReturningVisitor();

        this.layer = document.createElement("div");
        this.layer.className = "site-popup-layer";
        root.appendChild(this.layer);

        const visible = configs.filter((c) => this.passesFilters(c, returning));
        if (!visible.length) return;

        this.planDisplay(visible).forEach((unit) => this.scheduleUnit(unit));

        // 리사이즈 시 디바이스 전환 재보정(뷰포트 밖 방지 §5)
        window.addEventListener("resize", () => this.reclampAll(), { passive: true });
    }

    // ── 필터 ──────────────────────────────────────────────────────
    private passesFilters(cfg: PopupCfg, returning: boolean): boolean {
        const cl = cfg.client;
        // 디바이스
        if (cl.device_targets && cl.device_targets.length && cl.device_targets.indexOf(this.device) === -1) {
            return false;
        }
        // 방문자(로그인/비로그인은 회원기능 부재 시 통과 — §9)
        if (cl.visitor_target === "first" && returning) return false;
        if (cl.visitor_target === "returning" && !returning) return false;
        // 숨김 저장
        if (PopupStorage.isHidden(cfg.id, cl.content_version)) return false;
        return true;
    }

    // ── 표시 정책 ─────────────────────────────────────────────────
    private planDisplay(visible: PopupCfg[]): Unit[] {
        const units: Unit[] = [];
        const groups = new Map<string, PopupCfg[]>();
        const singles: PopupCfg[] = [];
        visible.forEach((c) => {
            if (c.group && c.group !== "") {
                const arr = groups.get(c.group) || [];
                arr.push(c);
                groups.set(c.group, arr);
            } else {
                singles.push(c);
            }
        });

        groups.forEach((cfgs) => {
            if (cfgs.length === 1) units.push({ kind: "single", cfg: cfgs[0] });
            else units.push({ kind: "group", cfgs });
        });

        // 최우선 1개만(§12): 단일 팝업 중 하나라도 priority 모드면 최상위만
        if (singles.some((s) => s.displayMode === "priority")) {
            if (singles[0]) units.push({ kind: "single", cfg: singles[0] });
        } else {
            singles.forEach((s) => units.push({ kind: "single", cfg: s }));
        }
        return units;
    }

    // ── 트리거 ────────────────────────────────────────────────────
    private scheduleUnit(unit: Unit): void {
        const rep = unit.kind === "single" ? unit.cfg : unit.cfgs[0];
        const trig = rep.client.trigger;
        let type = trig.type || "immediate";
        const value = trig.value || "";
        // 모바일에서 데스크톱 전용 이탈감지 강제 금지(§9)
        if (type === "exit" && (this.device === "mobile" || isTouch())) type = "immediate";

        const fire = (): void => this.showUnit(unit);

        if (type === "immediate") {
            window.requestAnimationFrame(fire);
            return;
        }
        if (type === "delay") {
            window.setTimeout(fire, Math.max(0, parseInt(value, 10) || 0));
            return;
        }
        if (type === "scroll") {
            const pct = Math.max(0, Math.min(100, parseInt(value, 10) || 30));
            const onScroll = (): void => {
                const doc = document.documentElement;
                const top = window.scrollY || doc.scrollTop || 0;
                const max = Math.max(1, (doc.scrollHeight || 0) - (window.innerHeight || 0));
                if (Math.round((top / max) * 100) >= pct) {
                    window.removeEventListener("scroll", onScroll);
                    fire();
                }
            };
            window.addEventListener("scroll", onScroll, { passive: true });
            onScroll();
            return;
        }
        if (type === "element") {
            const target = value ? document.querySelector(value) : null;
            if (!target || typeof IntersectionObserver === "undefined") {
                window.requestAnimationFrame(fire);
                return;
            }
            const io = new IntersectionObserver((entries) => {
                if (entries.some((e) => e.isIntersecting)) {
                    io.disconnect();
                    fire();
                }
            });
            io.observe(target);
            return;
        }
        if (type === "exit") {
            const onLeave = (e: MouseEvent): void => {
                if (e.clientY <= 0 && !e.relatedTarget) {
                    document.removeEventListener("mouseout", onLeave);
                    fire();
                }
            };
            document.addEventListener("mouseout", onLeave);
            return;
        }
        window.requestAnimationFrame(fire);
    }

    // ── 표시 ──────────────────────────────────────────────────────
    private showUnit(unit: Unit): void {
        try {
            if (unit.kind === "single") this.showPopup(unit.cfg);
            else this.showGroup(unit.cfgs);
        } catch {
            /* 개별 팝업 오류 격리 */
        }
    }

    private cloneBox(cfg: PopupCfg): HTMLElement | null {
        const tpl = this.root.querySelector<HTMLTemplateElement>(`template[data-popup-tpl="${cfg.id}"]`);
        if (!tpl) return null;
        const frag = tpl.content.cloneNode(true) as DocumentFragment;
        const box = frag.querySelector<HTMLElement>(".site-popup");
        return box;
    }

    private showPopup(cfg: PopupCfg): void {
        const box = this.cloneBox(cfg);
        if (!box) return;
        this.applyMedia(box, cfg);

        if (cfg.template === "full-modal") {
            const backdrop = document.createElement("div");
            backdrop.className = "site-popup-backdrop";
            backdrop.appendChild(box);
            this.layer.appendChild(backdrop);
            this.wireControls(box, cfg, backdrop);
            this.animateIn(box, cfg, backdrop);
            this.analytics.record(cfg.id, "impression");
            this.enterModal(box);
            return;
        }

        // 비모달: 위치는 slot 에, 애니메이션은 box 에 — transform 충돌 방지.
        const slot = this.mountPositioned(box, cfg);
        this.wireControls(box, cfg, null);
        this.animateIn(box, cfg, null);
        this.analytics.record(cfg.id, "impression");
        void slot;
    }

    /** box(또는 slider)를 위치 slot 으로 감싸 layer 에 추가. slot 을 반환. */
    private mountPositioned(inner: HTMLElement, cfg: PopupCfg): HTMLElement {
        const slot = document.createElement("div");
        slot.className = "site-popup-slot";
        slot.setAttribute("data-popup-slot", String(cfg.id));
        (slot as unknown as { __cfg?: PopupCfg; __box?: HTMLElement }).__cfg = cfg;
        (slot as unknown as { __cfg?: PopupCfg; __box?: HTMLElement }).__box = inner;
        this.applyPlacement(slot, cfg);
        slot.appendChild(inner);
        this.layer.appendChild(slot);
        return slot;
    }

    // 그룹 슬라이더(§12) — 한 카드에서 이전/다음 이동.
    private showGroup(cfgs: PopupCfg[]): void {
        const rep = cfgs[0];
        const slider = document.createElement("div");
        slider.className = "site-popup site-popup--slider";
        slider.setAttribute("role", "group");
        slider.setAttribute("aria-roledescription", "carousel");

        const track = document.createElement("div");
        track.className = "site-popup-slider__track";
        const slides: HTMLElement[] = [];
        cfgs.forEach((c) => {
            const box = this.cloneBox(c);
            if (!box) return;
            box.classList.add("site-popup-slider__slide");
            this.applyMedia(box, c);
            track.appendChild(box);
            slides.push(box);
        });
        if (!slides.length) return;
        slider.appendChild(track);

        const prev = document.createElement("button");
        prev.type = "button"; prev.className = "site-popup-slider__nav site-popup-slider__nav--prev";
        prev.setAttribute("aria-label", "이전"); prev.innerHTML = "‹";
        const next = document.createElement("button");
        next.type = "button"; next.className = "site-popup-slider__nav site-popup-slider__nav--next";
        next.setAttribute("aria-label", "다음"); next.innerHTML = "›";
        const dots = document.createElement("div");
        dots.className = "site-popup-slider__dots";
        slides.forEach((_, i) => {
            const d = document.createElement("button");
            d.type = "button"; d.className = "site-popup-slider__dot";
            d.setAttribute("aria-label", `${i + 1}번째`);
            d.addEventListener("click", () => go(i));
            dots.appendChild(d);
        });
        slider.append(prev, next, dots);

        let idx = 0;
        const render = (): void => {
            slides.forEach((s, i) => s.classList.toggle("is-active", i === idx));
            Array.from(dots.children).forEach((d, i) => d.classList.toggle("is-active", i === idx));
        };
        const go = (i: number): void => {
            idx = (i + slides.length) % slides.length;
            render();
            this.analytics.record(cfgs[idx].id, "slide_change");
        };
        prev.addEventListener("click", () => go(idx - 1));
        next.addEventListener("click", () => go(idx + 1));
        slider.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key === "ArrowLeft") go(idx - 1);
            else if (e.key === "ArrowRight") go(idx + 1);
        });
        render();

        this.mountPositioned(slider, rep);
        this.wireControls(slider, rep, null);
        this.animateIn(slider, rep, null);
        cfgs.forEach((c) => this.analytics.record(c.id, "impression"));
    }

    // ── 위치/크기 ─────────────────────────────────────────────────
    private applyPlacement(box: HTMLElement, cfg: PopupCfg): void {
        const p = cfg.placements[this.device] || cfg.placements.desktop || null;
        if (!p) { box.style.position = "fixed"; box.style.left = "50%"; box.style.top = "50%"; box.style.transform = "translate(-50%,-50%)"; box.style.zIndex = "1000"; return; }
        box.style.position = p.fixed ? "fixed" : "absolute";
        box.style.zIndex = String(p.z_index || 1000);
        box.style.left = box.style.right = box.style.top = box.style.bottom = "";
        const em = `${p.edge_margin || 0}px`;

        if (p.preset === "custom") {
            box.style.left = `${p.x_offset || 0}px`;
            box.style.top = `${p.y_offset || 0}px`;
            box.style.transform = "";
        } else {
            const [v, h] = presetParts(p.preset);
            if (h === "left") box.style.left = em;
            else if (h === "right") box.style.right = em;
            else box.style.left = "50%";
            if (v === "top") box.style.top = em;
            else if (v === "bottom") box.style.bottom = em;
            else box.style.top = "50%";
            const tx = h === "center" ? "-50%" : "0";
            const ty = v === "middle" || v === "center" ? "-50%" : "0";
            box.style.transform = `translate(${tx}, ${ty})`;
        }
        if (p.safe_area && this.device === "mobile") box.classList.add("site-popup--safe-area");
    }

    private applyMedia(box: HTMLElement, cfg: PopupCfg): void {
        const m = cfg.media[this.device] || cfg.media.desktop || null;
        if (!m) return;
        const cssLen = (v: string): string => (/^\d+$/.test(v) ? `${v}px` : v);
        if (m.width) box.style.width = cssLen(m.width);
        if (m.max_width) box.style.maxWidth = cssLen(m.max_width);
        if (m.mobile_full_width && this.device === "mobile") { box.style.width = "100%"; box.style.maxWidth = "100%"; }
        const img = box.querySelector<HTMLImageElement>("img");
        if (img) {
            if (m.height) img.style.height = cssLen(m.height);
            if (m.max_height) img.style.maxHeight = cssLen(m.max_height);
            if (m.object_fit) img.style.objectFit = m.object_fit;
            if (m.radius) img.style.borderRadius = cssLen(m.radius);
            if (m.aspect_ratio) img.style.aspectRatio = m.aspect_ratio.replace("/", " / ");
        }
        if (m.padding) box.style.setProperty("--popup-pad", cssLen(m.padding));
    }

    // ── 컨트롤 배선 ───────────────────────────────────────────────
    private wireControls(box: HTMLElement, cfg: PopupCfg, backdrop: HTMLElement | null): void {
        const cl = cfg.client;
        box.querySelector<HTMLButtonElement>("[data-popup-close]")?.addEventListener("click", () => this.dismiss(box, cfg, backdrop));
        box.querySelectorAll<HTMLAnchorElement>("[data-popup-cta]").forEach((a) => {
            a.addEventListener("click", () => this.analytics.record(cfg.id, "click"));
        });
        if (backdrop && cl.backdrop_close) {
            backdrop.addEventListener("click", (e) => { if (e.target === backdrop) this.dismiss(box, cfg, backdrop); });
        }
        if (cl.esc_close || cfg.template === "full-modal") {
            const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") { document.removeEventListener("keydown", onKey); this.dismiss(box, cfg, backdrop); } };
            document.addEventListener("keydown", onKey);
            (box as unknown as { __escHandler?: (e: KeyboardEvent) => void }).__escHandler = onKey;
        }
    }

    private dismiss(box: HTMLElement, cfg: PopupCfg, backdrop: HTMLElement | null): void {
        const cl = cfg.client;
        const today = box.querySelector<HTMLInputElement>("[data-popup-today]");
        if (today && today.checked) {
            PopupStorage.hide(cfg.id, cl.content_version, cl.hide_policy === "none" ? "today" : cl.hide_policy, cl.hide_days);
            this.analytics.record(cfg.id, cl.hide_policy === "today" ? "today_hide" : "period_hide");
        }
        this.analytics.record(cfg.id, "close");

        const done = (): void => {
            const esc = (box as unknown as { __escHandler?: (e: KeyboardEvent) => void }).__escHandler;
            if (esc) document.removeEventListener("keydown", esc);
            if (cfg.template === "full-modal") this.exitModal(box);
            const container = backdrop || box.closest<HTMLElement>(".site-popup-slot") || box;
            container.remove();
        };
        if (prefersReducedMotion()) { done(); return; }
        box.classList.remove("is-visible");
        box.classList.add("is-leaving");
        window.setTimeout(done, Math.min(400, cl.animation.duration || 300));
    }

    // ── 애니메이션 ────────────────────────────────────────────────
    private animateIn(box: HTMLElement, cfg: PopupCfg, backdrop: HTMLElement | null): void {
        const a = cfg.client.animation;
        if (prefersReducedMotion() || a.type === "none") {
            box.classList.add("is-visible");
            backdrop?.classList.add("is-visible");
            return;
        }
        box.style.setProperty("--popup-dur", `${a.duration || 300}ms`);
        box.style.setProperty("--popup-delay", `${a.delay || 0}ms`);
        box.classList.add("site-popup--anim", `site-popup--anim-${a.type}`);
        window.requestAnimationFrame(() => {
            window.requestAnimationFrame(() => {
                box.classList.add("is-visible");
                backdrop?.classList.add("is-visible");
            });
        });
    }

    // ── 모달 focus-trap / scroll lock(§15) ────────────────────────
    private enterModal(box: HTMLElement): void {
        this.openModals += 1;
        if (this.openModals === 1) {
            document.body.classList.add("popup-modal-open");
        }
        (box as unknown as { __prevFocus?: Element | null }).__prevFocus = document.activeElement;
        const focusables = box.querySelectorAll<HTMLElement>(FOCUSABLE);
        (focusables[0] || box).focus();
        const trap = (e: KeyboardEvent): void => {
            if (e.key !== "Tab") return;
            const list = Array.from(box.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null);
            if (!list.length) return;
            const first = list[0];
            const last = list[list.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        };
        box.addEventListener("keydown", trap);
    }

    private exitModal(box: HTMLElement): void {
        this.openModals = Math.max(0, this.openModals - 1);
        if (this.openModals === 0) document.body.classList.remove("popup-modal-open");
        const prev = (box as unknown as { __prevFocus?: HTMLElement }).__prevFocus;
        if (prev && typeof prev.focus === "function") prev.focus();
    }

    // ── 리사이즈 재보정 ───────────────────────────────────────────
    private reclampAll(): void {
        const dev = currentDevice();
        if (dev === this.device) return;
        this.device = dev;
        this.layer.querySelectorAll<HTMLElement>("[data-popup-slot]").forEach((slot) => {
            const ref = slot as unknown as { __cfg?: PopupCfg; __box?: HTMLElement };
            if (ref.__cfg) {
                this.applyPlacement(slot, ref.__cfg);
                if (ref.__box) this.applyMedia(ref.__box, ref.__cfg);
            }
        });
    }
}
